import React, { useState } from "react";
import { Button, Input, Select } from "../../../components";
import type {
  LoadSourceCreateInput,
  SourceType,
  NGTurbineType,
} from "../domain/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: LoadSourceCreateInput) => Promise<void>;
}

const INITIAL: LoadSourceCreateInput = {
  source_name: "",
  source_type: "solar",
  nameplate_capacity_mw: 100,
};

export function LoadAddSourceModal({ isOpen, onClose, onSubmit }: Props) {
  const [form, setForm] = useState<LoadSourceCreateInput>({ ...INITIAL });
  const [units, setUnits] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  if (!isOpen) return null;

  const set = (field: keyof LoadSourceCreateInput, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleClose = () => {
    setUnits(1);
    setProgress(null);
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    setProgress(null);
    try {
      const submitData = { ...form };
      // Ensure tech_type_id is set for solar/wind sources
      if ((submitData.source_type === "solar" || submitData.source_type === "wind") && !submitData.tech_type_id) {
        submitData.tech_type_id = submitData.source_type === "solar" ? "solar_ground" : "wind_onshore";
      }
      if (units <= 1) {
        await onSubmit(submitData);
      } else {
        let added = 0;
        try {
          for (let i = 1; i <= units; i++) {
            setProgress({ done: i - 1, total: units });
            await onSubmit({ ...submitData, source_name: `${submitData.source_name} #${i}` });
            added = i;
          }
        } catch (batchErr: any) {
          setError(
            added > 0
              ? `Added ${added} of ${units}. Failed on #${added + 1}: ${batchErr.message || "Unknown error"}`
              : batchErr.message || "Failed to add source"
          );
          return;
        }
      }
      setForm({ ...INITIAL });
      setUnits(1);
      setProgress(null);
      handleClose();
    } catch (err: any) {
      setError(err.message || "Failed to add source");
    } finally {
      setSubmitting(false);
    }
  };

  const sourceType = form.source_type;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface-700 rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-h4 text-text-primary mb-4">
            Add Generation Source
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Source Name"
              value={form.source_name}
              onChange={(e) => set("source_name", e.target.value)}
              placeholder="e.g., Solar Array A"
              required
            />

            <div className="grid grid-cols-3 gap-4">
              <Select
                label="Source Type"
                value={sourceType}
                onChange={(e) =>
                  set("source_type", e.target.value as SourceType)
                }
                options={[
                  { value: "solar", label: "Solar PV" },
                  { value: "wind", label: "Wind" },
                  { value: "ng", label: "Natural Gas" },
                  { value: "bess", label: "Battery Storage" },
                ]}
              />

              <Input
                label="Capacity (MW)"
                type="number"
                step="0.1"
                min="0.1"
                value={form.nameplate_capacity_mw}
                onChange={(e) => {
                  const cap = parseFloat(e.target.value) || 0;
                  setForm((prev) => ({
                    ...prev,
                    nameplate_capacity_mw: cap,
                    ...(prev.source_type === "bess" && {
                      bess_capacity_mwh: cap * (prev.bess_duration_hours ?? 4),
                    }),
                  }));
                }}
                required
              />

              <Input
                label="Units"
                type="number"
                step="1"
                min="1"
                max="50"
                value={units}
                onChange={(e) =>
                  setUnits(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))
                }
              />
            </div>

            {units > 1 && (
              <p className="text-body-sm text-text-muted">
                Will create: {form.source_name || "Source"} #1 through #{units} ({(units * form.nameplate_capacity_mw).toFixed(1)} MW total)
              </p>
            )}

            {/* Solar/Wind fields */}
            {(sourceType === "solar" || sourceType === "wind") && (
              <Select
                label="Technology Type"
                value={form.tech_type_id || (sourceType === "solar" ? "solar_ground" : "wind_onshore")}
                onChange={(e) => set("tech_type_id", e.target.value)}
                options={
                  sourceType === "solar"
                    ? [
                        { value: "solar_ground", label: "Ground-Mount Solar" },
                        { value: "solar_rooftop", label: "Rooftop Solar" },
                      ]
                    : [{ value: "wind_onshore", label: "Onshore Wind" }]
                }
              />
            )}

            {/* NG fields */}
            {sourceType === "ng" && (
              <>
                <Select
                  label="Turbine Type"
                  value={form.ng_turbine_type || "ccgt"}
                  onChange={(e) =>
                    set("ng_turbine_type", e.target.value as NGTurbineType)
                  }
                  options={[
                    { value: "ccgt", label: "Combined Cycle (CCGT)" },
                    { value: "scgt", label: "Simple Cycle (SCGT)" },
                    { value: "reciprocating", label: "Reciprocating Engine" },
                  ]}
                />
                <Input
                  label="Min Load Fraction"
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  value={form.ng_min_load_fraction ?? 0.3}
                  onChange={(e) =>
                    set("ng_min_load_fraction", parseFloat(e.target.value))
                  }
                />

                {/* SCR (Selective Catalytic Reduction) */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.ng_has_scr ?? false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForm((prev) => ({
                        ...prev,
                        ng_has_scr: checked,
                        ng_scr_nox_reduction_pct: checked ? (prev.ng_scr_nox_reduction_pct ?? 0.9) : undefined,
                      }));
                    }}
                    className="w-4 h-4 rounded border-border bg-surface-600 text-quantum-500 focus:ring-quantum-500"
                  />
                  <span className="text-body-sm text-text-primary">
                    SCR (Selective Catalytic Reduction)
                  </span>
                </label>

                {form.ng_has_scr && (
                  <div className="ml-6 space-y-2">
                    <Input
                      label="NOx Reduction Efficiency"
                      type="number"
                      step="0.01"
                      min="0.50"
                      max="0.99"
                      value={form.ng_scr_nox_reduction_pct ?? 0.9}
                      onChange={(e) =>
                        set("ng_scr_nox_reduction_pct", parseFloat(e.target.value))
                      }
                    />
                    <p className="text-body-sm text-text-muted">
                      Typical: 90% (0.90). Range: 50-99%. Reduces NOx-related health and ecosystem impacts.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* BESS fields */}
            {sourceType === "bess" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Duration (hours)"
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={form.bess_duration_hours ?? 4}
                    onChange={(e) => {
                      const dur = parseFloat(e.target.value) || 4;
                      setForm((prev) => ({
                        ...prev,
                        bess_duration_hours: dur,
                        bess_capacity_mwh: prev.nameplate_capacity_mw * dur,
                      }));
                    }}
                  />
                  <Input
                    label="Round-trip Eff"
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={form.bess_round_trip_eff ?? 0.85}
                    onChange={(e) =>
                      set("bess_round_trip_eff", parseFloat(e.target.value))
                    }
                  />
                </div>
                <div className="bg-surface-600 rounded-lg px-3 py-2 text-body-sm text-text-muted">
                  Storage:{" "}
                  <span className="text-text-primary font-medium">
                    {(form.nameplate_capacity_mw * (form.bess_duration_hours ?? 4)).toFixed(0)} MWh
                  </span>
                  <span className="ml-1">
                    ({form.nameplate_capacity_mw} MW &times; {form.bess_duration_hours ?? 4}h)
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="text-red-400 text-body-sm">{error}</div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={handleClose} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting && progress
                  ? `Adding ${progress.done + 1} of ${progress.total}...`
                  : submitting
                    ? "Adding..."
                    : units > 1
                      ? `Add ${units} Sources`
                      : "Add Source"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
